/**
 * Billing Service
 * Handles subscription management and billing operations
 */

import apiClient from './apiClient';

/**
 * Map frontend account types to backend plan types
 */
const mapPlanType = (frontendAccountType) => {
  const planMapping = {
    // Frontend format -> Backend format
    'solo': 'Solo',
    'solo_assistant': 'Solo_Assistant',
    'small_team': 'Small_Team',
    'medium_team': 'Medium_Team',
    'large_team': 'Large_Team',
    'small_brokerage': 'Small_Brokerage',
    'medium_brokerage': 'Medium_Brokerage',
    'large_brokerage': 'Large_Brokerage'
  };
  
  return planMapping[frontendAccountType] || frontendAccountType;
};

/**
 * Create subscription for a user after successful signup with validation
 */
export const createSubscription = async (userData) => {
  try {
    console.log('💳 Creating subscription for user:', userData.user_id);
    
    // Check if token is available
    const storedToken = localStorage.getItem('token');
    console.log('🔍 Token available for subscription call:', storedToken ? 'YES' : 'NO');
    if (storedToken) {
      console.log('🔍 Token preview:', storedToken.substring(0, 20) + '...');
    }
    
    // Map frontend account type to backend plan type
    const backendPlanType = mapPlanType(userData.accountType);
    console.log('🔄 Plan type mapping:', userData.accountType, '->', backendPlanType);
    
    // Validate subscription creation
    const isTrialAccount = userData.accountType?.toLowerCase() === 'trial' || 
                          userData.accountType?.toLowerCase().includes('trial');
    const isPaidPlan = backendPlanType !== 'Trial' && !backendPlanType.toLowerCase().includes('trial');
    
    // For trial users upgrading to paid plans, ensure payment method is provided
    if (isTrialAccount && isPaidPlan && !userData.paymentMethodId) {
      console.warn('⚠️ Trial user attempting to create paid subscription without payment method');
      throw new Error('PAYMENT_METHOD_REQUIRED: Payment method is required for trial users upgrading to paid plans');
    }
    
    const subscriptionData = {
      user_id: userData.user_id,
      plan_type: backendPlanType,  // Use mapped plan type
      billing_cycle: userData.billingCycle || 'monthly',
      payment_method_id: userData.paymentMethodId
    };
    
    console.log('📤 Subscription data:', subscriptionData);
    console.log('🔍 Validation passed:', { isTrialAccount, isPaidPlan, hasPaymentMethod: !!userData.paymentMethodId });
    
    const response = await apiClient.post(`/api/billing/${userData.user_id}/subscription`, subscriptionData);
    
    console.log('✅ Subscription created successfully:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Subscription creation failed:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    console.error('❌ Request headers:', error.config?.headers);
    
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to create subscription'
    };
  }
};

/**
 * Get billing configuration from backend
 */
export const getBillingConfig = async () => {
  try {
    const response = await apiClient.get('/api/billing/config');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get billing config:', error);
    return {
      success: false,
      message: 'Failed to load billing configuration'
    };
  }
};

/**
 * Get user's billing information
 */
export const getBillingInfo = async (userId) => {
  try {
    const response = await apiClient.get(`/api/billing/${userId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get billing info:', error);
    return {
      success: false,
      message: 'Failed to load billing information'
    };
  }
};

/**
 * Update subscription
 */
export const updateSubscription = async (userId, subscriptionData) => {
  try {
    const response = await apiClient.put(`/api/billing/${userId}/subscription`, subscriptionData);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to update subscription:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update subscription'
    };
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (userId, cancelAtPeriodEnd = true) => {
  try {
    const response = await apiClient.put(`/api/billing/${userId}/subscription/cancel`, {
      cancel_at_period_end: cancelAtPeriodEnd
    });
    return response.data;
  } catch (error) {
    console.error('❌ Failed to cancel subscription:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to cancel subscription'
    };
  }
};

const billingService = {
  createSubscription,
  getBillingConfig,
  getBillingInfo,
  updateSubscription,
  cancelSubscription
};

export default billingService;